import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { 
  ShieldCheck, ArrowRight, MapPin, Check, Map as MapIcon, Plus, Minus,
  Target, Users, Filter, Eye, Lock, RefreshCw
} from "lucide-react";
import { api, asItems, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import MegaSearchBar from "@/components/MegaSearchBar";
import HomeDiscoverySections from "@/components/home/HomeDiscoverySections";
import TestimonialsCarousel from "@/components/home/TestimonialsCarousel";
import RecentSearchesSection from "@/components/home/RecentSearchesSection";
import TrendingPropertiesSection from "@/components/home/TrendingPropertiesSection";
import NewlyAddedSection from "@/components/home/NewlyAddedSection";
import VerifiedAgentsSection from "@/components/home/VerifiedAgentsSection";
import { motion, AnimatePresence } from "framer-motion";

const DEFAULT_HERO_SLIDES = [
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2200&q=72",
  "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=2200&q=72",
  "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=2200&q=72",
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=2200&q=72",
];

// Hosting can supply licensed brand photography as comma-separated URLs.
// Built-in editorial images keep local development usable without a separate
// configuration service.
function configuredImages(value, fallback) {
  const urls = String(value || "").split(",").map((url) => url.trim()).filter(Boolean);
  return urls.length ? urls : fallback;
}

const HERO_SLIDES = configuredImages(process.env.REACT_APP_HOME_HERO_IMAGE_URLS, DEFAULT_HERO_SLIDES);

export default function Home() {
  const [activeTrendingCity, setActiveTrendingCity] = useState("");
  const [faqOpen, setFaqOpen] = useState(null);
  // Issue #11: homepage FAQ must come from the SAME /faqs endpoint/database as
  // the /faq page linked in the footer. Start empty and populate only from the
  // API so the two never diverge with different hardcoded content.
  const [dbFaqs, setDbFaqs] = useState([]);
  const [mapProperties, setMapProperties] = useState([]);
  const [mapError, setMapError] = useState("");
  const [mapLoading, setMapLoading] = useState(true);
  const [discoveryReloadKey, setDiscoveryReloadKey] = useState(0);
  const [faqError, setFaqError] = useState("");
  const [faqLoading, setFaqLoading] = useState(true);
  const [faqReloadKey, setFaqReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setMapError("");
    setMapLoading(true);
    api.get('/properties?land_only=true&verified_documents=true&limit=4').then(res => {
      const items = asItems(res.data);
      if (active) setMapProperties(Array.isArray(items) ? items.slice(0, 4) : []);
    }).catch((error) => {
      if (active) {
        setMapProperties([]);
        setMapError(formatApiError(error));
      }
    }).finally(() => { if (active) setMapLoading(false); });

    return () => { active = false; };
  }, [discoveryReloadKey]);

  useEffect(() => {
    let active = true;
    setFaqError("");
    setFaqLoading(true);
    api.get('/faqs')
      .then(res => {
        const items = res?.data?.items || res?.data?.data || res?.data || [];
        if (active) {
          // Show a homepage-friendly subset; full list lives on /faq.
          setDbFaqs(Array.isArray(items) ? items.slice(0, 6).map(item => ({ q: item.question, a: item.answer })) : []);
        }
      })
      .catch((error) => {
        if (active) {
          setDbFaqs([]);
          setFaqError(formatApiError(error));
        }
      })
      .finally(() => { if (active) setFaqLoading(false); });
    return () => { active = false; };
  }, [faqReloadKey]);

  return (
    <div data-testid="home-page" className="premium-page-surface min-h-screen relative overflow-hidden">
      {/* Hero Search Section */}
      <section className="relative w-full min-h-[82vh] sm:min-h-[85vh] overflow-hidden flex items-center justify-center border-b border-slate-200/50" data-testid="hero-section">
        <div className="absolute inset-0" aria-hidden="true">
          <div className="hero-slide" style={{ backgroundImage: `url('${HERO_SLIDES[0]}')` }} data-testid="hero-slide-0" />
          <div className="hero-overlay bg-gradient-to-b from-[#0b1329]/85 via-[#0b1329]/65 to-[#0b1329]/95" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:3rem_3rem]" />
        </div>

        {/* Floating brand emblem */}
        <div className="hero-brand-badge" aria-hidden="true">
          <img src="/brand/real-place-mark.svg" alt="" />
        </div>

        <div className="relative z-10 w-[96%] max-w-[1400px] mx-auto px-4 pt-8 pb-14 sm:pt-10 sm:pb-20 text-center text-white" data-testid="hero-content">
          <div className="hero-eyebrow-premium inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-6 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.2em] shadow-lg shadow-black/20 hover:bg-white/15 transition-all duration-300 hover:scale-[1.02] cursor-default" data-testid="hero-eyebrow">
            <MapPin className="w-4 h-4 text-amber-300" /> Land &amp; Plot Marketplace
          </div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight leading-[0.92] text-white text-balance drop-shadow-[0_4px_30px_rgba(0,0,0,0.5)]" data-testid="hero-heading">
            Find the right place to <span className="champagne-text drop-shadow-none">invest.</span>
          </h1>
          <p className="mt-3 text-xs sm:text-sm lg:text-base text-slate-200 max-w-2xl mx-auto font-medium leading-relaxed drop-shadow-sm">
            Search cities, localities, projects and landmarks across published ShreeHomes inventory.
          </p>

          <div className="mt-8 w-full max-w-[1200px] mx-auto text-left overflow-visible" data-testid="hero-search-wrapper">
            <MegaSearchBar />
          </div>
        </div>
      </section>

      <HomeDiscoverySections />

      {/* 2. Recently Searched Properties (LocalStorage History) */}
      <RecentSearchesSection />

      {/* 3. Trending Properties & Plots by City */}
      <TrendingPropertiesSection 
        activeCity={activeTrendingCity} 
        setActiveCity={setActiveTrendingCity} 
      />

      {/* Newly Added Properties Section */}
      <NewlyAddedSection />

      {/* Redesigned Horizontal Marketplace Sections (Stacked & Light Theme) */}
      <section className="py-16 space-y-12 bg-slate-50/40" data-testid="split-marketplace-banner">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          
          {/* Plots on Demand section */}
          <div className="bg-gradient-to-br from-[#f6fdf9] via-white to-[#f9fefb] border border-[#e2f7ea] rounded-3xl p-8 md:p-12 shadow-sm flex flex-col lg:flex-row items-center gap-10 hover:shadow-md transition duration-300">
            {/* Left Column (Content) */}
            <div className="lg:w-[60%] space-y-5 text-left">
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#10b981] flex items-center gap-1.5">
                🔍 CAN'T FIND THE RIGHT PLOT?
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900 leading-tight">
                Plots on <span className="text-[#10b981]">Demand</span>
              </h2>
              <p className="text-slate-500 text-sm max-w-xl leading-relaxed">
                Share your requirements to search published plot inventory by budget, size, and location.
              </p>

              {/* Features Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-3">
                {/* Feature 1 */}
                <div className="flex gap-3 items-start">
                  <div className="w-10 h-10 rounded-xl bg-[#e6f9ed] text-[#10b981] flex items-center justify-center shrink-0">
                    <Target className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 text-sm">Personalized Matching</h4>
                    <p className="text-slate-500 text-[11px] mt-1 leading-normal font-semibold">We match plots based on your budget, size &amp; preferred location.</p>
                  </div>
                </div>

                {/* Feature 2 */}
                <div className="flex gap-3 items-start">
                  <div className="w-10 h-10 rounded-xl bg-[#e6f9ed] text-[#10b981] flex items-center justify-center shrink-0">
                    <Users className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 text-sm">Verified Agent Support</h4>
                    <p className="text-slate-500 text-[11px] mt-1 leading-normal font-semibold">Connect with verified agents for property guidance and follow-up.</p>
                  </div>
                </div>

                {/* Feature 3 */}
                <div className="flex gap-3 items-start">
                  <div className="w-10 h-10 rounded-xl bg-[#e6f9ed] text-[#10b981] flex items-center justify-center shrink-0">
                    <Filter className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 text-sm">Exact Budget Filtering</h4>
                    <p className="text-slate-500 text-[11px] mt-1 leading-normal font-semibold">We shortlist plots within the budget range you provide.</p>
                  </div>
                </div>
              </div>

              {/* CTA footer area */}
              <div className="flex flex-col sm:flex-row items-center gap-5 pt-5 border-t border-slate-100">
                <Button asChild className="w-full sm:w-auto bg-[#10b981] hover:bg-[#059669] text-white font-extrabold px-8 py-6 rounded-xl shadow-md hover:scale-[1.01] active:scale-95 transition-all duration-200 text-xs md:text-sm">
                  <Link to="/plot-on-demand">Submit Plot Requirement <ArrowRight className="w-4 h-4" /></Link>
                </Button>
                <div className="flex items-center gap-1.5 text-[#10b981] text-xs font-bold">
                  <span className="text-[10px]">🛡️</span>
                  <span>Track the request from your dashboard</span>
                </div>
              </div>
            </div>

            {/* Right Column (Isometric Map Illustration) */}
            <div className="lg:w-[40%] w-full flex justify-center max-w-[320px] lg:max-w-none">
              <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto drop-shadow-sm pointer-events-none select-none">
                {/* Ground platform */}
                <path d="M200 60 L350 140 L200 220 L50 140 Z" fill="#e6f9ed" />
                <path d="M200 60 L350 140 L200 220 L50 140 Z" stroke="#a7f3d0" strokeWidth="2" />
                
                {/* Land plots grid divisions */}
                <path d="M125 100 L275 180" stroke="#a7f3d0" strokeWidth="1.5" />
                <path d="M275 100 L125 180" stroke="#a7f3d0" strokeWidth="1.5" />
                <path d="M200 60 L200 220" stroke="#a7f3d0" strokeWidth="1.5" strokeDasharray="3 3" />
                <path d="M50 140 L350 140" stroke="#a7f3d0" strokeWidth="1.5" strokeDasharray="3 3" />
                
                {/* Little trees */}
                <circle cx="85" cy="115" r="10" fill="#22c55e" opacity="0.8" />
                <rect x="83" y="115" width="4" height="12" fill="#15803d" />

                <circle cx="315" cy="165" r="12" fill="#22c55e" opacity="0.8" />
                <rect x="313" y="165" width="4" height="14" fill="#15803d" />
                
                {/* Pulsing Locator Circle */}
                <ellipse cx="200" cy="140" rx="20" ry="10" fill="#10b981" opacity="0.15" />
                
                {/* Green map pin */}
                <path d="M200 140 C190 120 185 108 185 95 C185 82 191 73 200 73 C209 73 215 82 215 95 C215 108 210 120 200 140 Z" fill="#10b981" />
                <circle cx="200" cy="95" r="5" fill="#ffffff" />
              </svg>
            </div>
          </div>

          {/* Sell / List Your Property Section */}
          <div className="bg-gradient-to-br from-[#f3f7ff] via-white to-[#f9faff] border border-[#e2ecf7] rounded-3xl p-8 md:p-12 shadow-sm flex flex-col lg:flex-row items-center gap-10 hover:shadow-md transition duration-300 mt-12">
            {/* Left Column (Modern Villa Illustration) */}
            <div className="lg:w-[40%] w-full flex justify-center order-2 lg:order-1 max-w-[320px] lg:max-w-none">
              <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto drop-shadow-sm pointer-events-none select-none">
                {/* Sky circle glow */}
                <circle cx="200" cy="150" r="100" fill="#eff6ff" />
                
                {/* House Base Platform */}
                <rect x="80" y="210" width="240" height="8" rx="4" fill="#cbd5e1" />
                
                {/* Modern Architecture */}
                <rect x="100" y="110" width="160" height="100" rx="8" fill="#ffffff" />
                <rect x="92" y="130" width="138" height="80" rx="4" fill="#f1f5f9" />
                <rect x="160" y="100" width="105" height="60" rx="4" fill="#dbeafe" />
                
                {/* Balcony structure */}
                <rect x="90" y="170" width="142" height="4" fill="#94a3b8" />
                <rect x="95" y="145" width="30" height="25" fill="#3b82f6" opacity="0.25" />
                <rect x="135" y="145" width="30" height="25" fill="#3b82f6" opacity="0.25" />
                
                {/* Wood front door */}
                <rect x="175" y="160" width="25" height="50" fill="#475569" />
                <circle cx="195" cy="185" r="2" fill="#e2e8f0" />
                
                {/* For Sale Sign */}
                <rect x="250" y="160" width="70" height="40" rx="4" fill="#3b82f6" />
                <rect x="282" y="200" width="6" height="20" fill="#64748b" />
                <text x="285" y="184" fill="#ffffff" fontSize="9" fontWeight="900" textAnchor="middle" fontFamily="sans-serif">FOR SALE</text>

                {/* Decorative Foliage */}
                <circle cx="75" cy="195" r="15" fill="#10b981" opacity="0.8" />
                <circle cx="325" cy="195" r="12" fill="#10b981" opacity="0.8" />
              </svg>
            </div>

            {/* Right Column (Content) */}
            <div className="lg:w-[60%] space-y-5 text-left order-1 lg:order-2">
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#2563eb] flex items-center gap-1.5">
                🏷️ WANT TO SELL OR RENT?
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900 leading-tight">
                Sell / List <span className="text-[#2563eb]">Your Property</span>
              </h2>
              <p className="text-slate-500 text-sm max-w-xl leading-relaxed">
                List your property or land for discovery by registered buyers, agents, and offices.
              </p>

              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-5 pt-3">
                {/* Feature 1 */}
                <div className="flex gap-2.5 items-start">
                  <div className="w-8 h-8 rounded-lg bg-[#eff6ff] text-[#2563eb] flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 text-[13px]">Verification Workflow</h4>
                    <p className="text-slate-500 text-[10px] leading-normal font-semibold">Eligible listings can earn verification badges after review.</p>
                  </div>
                </div>

                {/* Feature 2 */}
                <div className="flex gap-2.5 items-start">
                  <div className="w-8 h-8 rounded-lg bg-[#eff6ff] text-[#2563eb] flex items-center justify-center shrink-0">
                    <Eye className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 text-[13px]">Listing Discovery</h4>
                    <p className="text-slate-500 text-[10px] leading-normal font-semibold">Published listings become available across search and discovery pages.</p>
                  </div>
                </div>

                {/* Feature 3 */}
                <div className="flex gap-2.5 items-start">
                  <div className="w-8 h-8 rounded-lg bg-[#eff6ff] text-[#2563eb] flex items-center justify-center shrink-0">
                    <Users className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 text-[13px]">Direct Matching</h4>
                    <p className="text-slate-500 text-[10px] leading-normal font-semibold">Receive direct inquiries from interested buyers.</p>
                  </div>
                </div>

                {/* Feature 4 */}
                <div className="flex gap-2.5 items-start">
                  <div className="w-8 h-8 rounded-lg bg-[#eff6ff] text-[#2563eb] flex items-center justify-center shrink-0">
                    <Lock className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 text-[13px]">Secure & Private</h4>
                    <p className="text-slate-500 text-[10px] leading-normal font-semibold">We never share your contact details publicly.</p>
                  </div>
                </div>
              </div>

              {/* CTA footer area */}
              <div className="flex flex-col sm:flex-row items-center gap-5 pt-5 border-t border-slate-100">
                <Button asChild className="w-full sm:w-auto bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-extrabold px-8 py-6 rounded-xl shadow-md hover:scale-[1.01] active:scale-95 transition-all duration-200 text-xs md:text-sm">
                  <Link to="/dashboard/property/new?type=land">List Your Property <ArrowRight className="w-4 h-4" /></Link>
                </Button>
                <div className="flex items-center gap-1.5 text-[#2563eb] text-xs font-bold">
                  <span className="text-[10px]">🛡️</span>
                  <span>Account-based enquiry workflow</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 6. Combined Verification Timeline & Map Preview */}
      <section className="py-16 bg-white/40 border-b border-slate-200/40" data-testid="verification-and-map-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            
            {/* Left column: Timeline */}
            <div className="lg:col-span-7 flex flex-col">
              <div className="border-l-4 border-emerald-500 pl-4 mb-10">
                <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900">
                  How Listing Verification Works
                </h2>
                <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                  Submitted listings follow the staged review states shown below before publication.
                </p>
              </div>

              <div className="relative pl-2">
                {/* The vertical connection line */}
                <div className="absolute left-[21px] top-6 bottom-6 w-0.5 bg-slate-200" />

                <div className="space-y-8">
                  {[
                    {
                      n: "1",
                      title: "Submit Listing Details",
                      desc: "An authorized account supplies property information and any required documents."
                    },
                    {
                      n: "2",
                      title: "Owner Verification",
                      desc: "The listing waits for the required owner and account verification state."
                    },
                    {
                      n: "3",
                      title: "Office Review",
                      desc: "The assigned office reviews the submitted listing information and records its decision."
                    },
                    {
                      n: "4",
                      title: "Admin Review & Publication",
                      desc: "An administrator completes the final configured review before an eligible listing is published."
                    }
                  ].map((step, idx) => (
                    <div key={idx} className="flex gap-6 relative items-start group">
                      {/* Circle badge */}
                      <div 
                        className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm z-10 shrink-0 shadow-sm border border-white transition duration-300 ${
                          step.n === "4" 
                            ? "bg-emerald-600 text-white" 
                            : "bg-[#0078DB] text-white"
                        }`}
                      >
                        {step.n === "4" ? (
                          <Check className="w-5 h-5 text-white stroke-[3px]" />
                        ) : (
                          step.n
                        )}
                      </div>
                      
                      {/* Text block */}
                      <div>
                        <h3 className="font-display font-bold text-sm sm:text-base text-slate-900 group-hover:text-[#0078DB] transition-colors">
                          {step.title}
                        </h3>
                        <p className="text-slate-500 text-xs sm:text-sm mt-1.5 leading-relaxed max-w-xl">
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right column: Dark map card */}
            <div className="lg:col-span-5 w-full mt-10 lg:mt-0">
              <div className="premium-dark-panel bg-[#051329] rounded-3xl p-8 lg:p-10 text-white relative overflow-hidden shadow-xl flex flex-col justify-between h-full border border-white/5">
                {/* Background patterns */}
                <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_center,#ffffff_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none" />
                
                <div>
                  <span className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-400">
                    Visual Search Portal
                  </span>
                  <h3 className="font-display text-2xl font-bold tracking-tight text-white mt-1">
                    Explore Published Plots on Map
                  </h3>
                  <p className="text-slate-300 text-xs mt-3 leading-relaxed">
                    Open the map view for published land listings that include stored location coordinates.
                  </p>
                </div>

                {/* This preview lists real records without pretending decorative
                    positions are geographic coordinates. The full map uses the
                    stored latitude/longitude values. */}
                <div className="min-h-52 rounded-2xl bg-slate-950/80 border border-white/10 shadow-inner relative overflow-hidden mt-6 p-4">
                  <div className="absolute inset-0 opacity-[0.05] bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:1.2rem_1.2rem]" />
                  <div className="relative z-10 space-y-2">
                    {mapLoading ? (
                      <div className="min-h-44 animate-pulse rounded-xl bg-white/5" aria-label="Loading map preview"/>
                    ) : mapError ? (
                      <div className="min-h-44 grid place-items-center text-center px-5" role="alert">
                        <div><MapIcon className="w-8 h-8 mx-auto text-amber-300" /><p className="text-xs font-bold text-white mt-3">Map preview could not be loaded</p><p className="text-[10px] text-slate-300 mt-1">{mapError}</p><Button type="button" size="sm" variant="secondary" className="mt-3 gap-2" onClick={() => setDiscoveryReloadKey((value) => value + 1)}><RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />Retry</Button></div>
                      </div>
                    ) : mapProperties.length > 0 ? mapProperties.slice(0, 3).map((property) => (
                      <Link key={property.property_id || property.id} to={`/properties/${property.property_id || property.id}`}
                        title={`View ${property.title || "published plot"}`}
                        className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-3 hover:bg-white/10 transition">
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-white truncate">{property.title || "Published land listing"}</p>
                          <p className="text-[10px] text-slate-400 truncate">{[property.locality, property.city].filter(Boolean).join(", ")}</p>
                        </div>
                        <MapPin className="w-4 h-4 text-emerald-400 shrink-0" />
                      </Link>
                    )) : (
                      <div className="min-h-44 grid place-items-center text-center px-5">
                        <div><MapIcon className="w-8 h-8 mx-auto text-emerald-400" /><p className="text-xs font-bold text-white mt-3">No mapped plot listings yet</p><p className="text-[10px] text-slate-400 mt-1">Published inventory with stored coordinates will appear here.</p></div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Button CTA */}
                <div className="mt-6">
                  <Button asChild className="w-full font-bold gap-2 py-6 rounded-2xl bg-white text-[#051329] hover:bg-white/95 hover:scale-[1.01] transition-all duration-200 shadow-md border-0" data-testid="explore-map-cta">
                    <Link to="/properties?view=map&land_only=true&verified_documents=true"><MapIcon className="w-4 h-4 text-[#0078DB]" /> Open Full Map Search</Link>
                  </Button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 7. Verified Agents Section */}
      <VerifiedAgentsSection activeCity={activeTrendingCity} />

      {/* 8. Buyer Testimonials Section */}
      <TestimonialsCarousel />

      {/* 9. Interactive FAQ Accordion */}
      <section className="py-16 bg-white/40" data-testid="faq-accordion-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="gold-divider max-w-2xl mb-10 border-l-4 pl-4">
            <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900">
              Frequently Asked Questions
            </h2>
            <p className="text-xs text-muted-foreground mt-1.5">
              Read the currently published guidance for listings, enquiries, accounts, and Site Visits.
            </p>
          </div>

          {faqLoading && <div className="grid gap-3" aria-label="Loading FAQs">{[0, 1, 2].map((item) => <div key={item} className="h-14 animate-pulse rounded-2xl bg-slate-200/70"/>)}</div>}
          {!faqLoading && faqError && <div className="mb-4 rounded-2xl border border-amber-300/60 bg-amber-50 p-6 text-center" role="alert"><p className="text-sm font-bold text-slate-900">FAQs could not be loaded.</p><p className="mt-1 text-xs text-slate-600">{faqError}</p><Button type="button" size="sm" variant="outline" className="mt-4 gap-2" onClick={() => setFaqReloadKey((value) => value + 1)}><RefreshCw className="h-4 w-4" aria-hidden="true" />Retry</Button></div>}
          {!faqLoading && !faqError && dbFaqs.length === 0 && <p className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-600">No published FAQs are available yet.</p>}
          <div className="space-y-3">
            {!faqLoading && !faqError && dbFaqs.map((item, idx) => {
              const isOpen = faqOpen === idx;
              return (
                <div 
                  key={idx}
                  className={`rounded-2xl border transition bg-white shadow-sm overflow-hidden ${
                    isOpen ? "border-[#0078DB]/40 shadow-sm" : "border-slate-200"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setFaqOpen(isOpen ? null : idx)}
                    aria-expanded={isOpen}
                    className="w-full flex items-center justify-between p-5 text-left text-xs sm:text-sm font-bold text-slate-800 hover:text-primary transition"
                  >
                    <span>{item.q}</span>
                    {isOpen ? (
                      <Minus className="w-4 h-4 text-[#0078DB] shrink-0 ml-2" />
                    ) : (
                      <Plus className="w-4 h-4 text-[#0078DB] shrink-0 ml-2" />
                    )}
                  </button>
                  
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                      >
                        <div className="px-5 pb-5 pt-1 border-t border-slate-100 text-xs text-slate-600 leading-relaxed font-semibold">
                          {item.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          <div className="mt-8">
            <Link to="/faq" className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0078DB] hover:text-[#005cab] transition" data-testid="home-view-all-faqs">
              View all FAQs <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>



    </div>
  );
}
